import { AggregatedAvailabilitySlotsRequest } from '@kyruus/scheduling-components';
import { Facet, FacetTerm, Provider } from '@kyruus/search-sdk';

import {
  AggregatedPurposeOptionsByRelationship,
  AggregatedSlot,
  PatientRelationship,
  PatientRelationshipEnum,
  PMCConfig,
  ProviderMatchProviderV9,
  VisitType
} from '@kyruus/types';
import { MarketableLocation } from '../../types';
import { getRelativeParameterizedBookingUrl } from '../../utils/getRelativeParameterizedBookingUrl';
import { Location } from 'history';

export function getProviderLocationIdsByPrimaryLocation(
  providers: Provider[],
  primaryLocationId = ''
): number[] {
  const providerLocationIds = new Set<number>();

  providers
    .flatMap((p) => p.locations)
    .forEach((location) => {
      // TODO: Fix incorrect number type on @kyruus/search-sdk/LocationResult.primary_marketable_location_id
      if (
        (location?.primary_marketable_location_id as unknown as string) ===
        primaryLocationId
      ) {
        providerLocationIds.add(location.id);
      }
    });

  return [...providerLocationIds];
}

// For now if there is more than one provider with that single time slot we will pick them at "random"
export const getRandomizedProviderId = (providerIds: number[]) => {
  const randomIndex = Math.floor(Math.random() * providerIds.length);
  return providerIds[randomIndex];
};

export const getSelectedAppointmentHref = (
  slot: AggregatedSlot,
  locationProviders: ProviderMatchProviderV9[],
  windowLocation: Location,
  config: PMCConfig
): string => {
  const { provider_ids, purpose, patient_relationship, date, start } = slot;

  const providerId =
    provider_ids.length > 1
      ? getRandomizedProviderId(provider_ids)
      : provider_ids[0];

  const matchingProvider = locationProviders.find((provider) => {
    return provider.id === providerId;
  });

  const urlParams = new URLSearchParams(windowLocation.search);
  const dateTime = `${date} ${start}`;
  urlParams.set('period_start', dateTime);

  const bookingUrl =
    getRelativeParameterizedBookingUrl({
      apptOptions: {
        purpose,
        relationship: patient_relationship as PatientRelationship
      },
      provider: {
        book_online_url: matchingProvider?.book_online_url ?? '',
        id: providerId
      },
      location: {
        pathname: windowLocation.pathname,
        search: urlParams.toString()
      },
      config
    }) ?? '';

  return bookingUrl;
};

export const mapSlotFacetsToAggregatedSlots = (
  facets: Facet[],
  slotsRequest: AggregatedAvailabilitySlotsRequest
): AggregatedSlot[] => {
  const aggregatedSlots: AggregatedSlot[] = [];
  facets[0].terms.forEach((locationTerm: FacetTerm) => {
    const location_id = locationTerm.value as number;

    locationTerm.terms.forEach((dateTerm: FacetTerm) => {
      const date = dateTerm.value as string;

      dateTerm.terms.forEach((startTerm: FacetTerm) => {
        const start = startTerm.value as string;

        // Collect all provider IDs for this time slot
        const provider_ids = startTerm.terms.map(
          (providerTerm: FacetTerm) => providerTerm.value as number
        );

        aggregatedSlots.push({
          location_id,
          date,
          start,
          end: '',
          provider_ids,
          purpose: slotsRequest.purpose.name,
          patient_relationship: slotsRequest.patientRelationship,
          visit_method: slotsRequest.purpose.visit_method
        });
      });
    });
  });

  return aggregatedSlots;
};

export const mapSlotFacetsToAggregatedPurposes = (
  facets: Facet[]
): AggregatedPurposeOptionsByRelationship => {
  const initialPurposes: AggregatedPurposeOptionsByRelationship = {
    [PatientRelationshipEnum.ESTABLISHED]: [],
    [PatientRelationshipEnum.NEW]: []
  };

  if (facets.length === 0) {
    return initialPurposes;
  }

  return facets?.[0]?.terms?.reduce((acc, relationship) => {
    const relationshipKey = relationship.value as PatientRelationshipEnum;

    const purposeOptions = relationship.terms.map((purpose: FacetTerm) => {
      const providerIds = purpose?.terms?.[0]?.terms.map(
        (term: FacetTerm) => term.value as number
      );

      return {
        name: purpose.value as string,
        visit_method: purpose?.terms?.[0]?.value as VisitType,
        providerIds
      };
    });

    acc[relationshipKey] = purposeOptions;
    return acc;
  }, initialPurposes);
};

export const getContactForLocation = (
  location: MarketableLocation,
  contactType: string = 'phone'
): string | undefined => {
  const selectedContact = location.contacts?.find(
    (contact) => contact.contact_type === contactType
  );

  return selectedContact?.value;
};
